import {
    getInitialData, setContainerHeader, debounce, scwFetch, resetContainers,
    clearDynamicContainer, toggleSidePanel, toggleDynamicContainerMode,
    errorLabelFragment, dynamicContainer,
    clearContainerElement
} from '../main';
import {checkPasswordRules} from "../utils/validators";
import {onSubmit} from "../utils/form";
import {initPasswordToggles} from "../utils/password-toggle";

interface BaseChangePasswordState {
    state: string;
    environmentReadableName: string;
    hint: string;
    actionUrl: string;
    backToLogin: string;
    username: string;
}

interface ForgotPasswordChangePasswordState extends BaseChangePasswordState {
    hint: "forgot-password"
}
interface ChangePasswordState extends BaseChangePasswordState {
    hint: "change_pass_req"
}

type PageState = ForgotPasswordChangePasswordState | ChangePasswordState;

const state = getInitialData<PageState>();

const templates = {
    changePasswordForm: document.getElementById("template_changePasswordForm") as HTMLTemplateElement,
    changePasswordSuccess: document.getElementById("template_changePasswordSuccess") as HTMLTemplateElement,
};

setContainerHeader({title: 'Create New Password', subTitle: 'Please create your new password.'});

const formFragment = templates.changePasswordForm?.content?.cloneNode(true) as DocumentFragment;
const form = formFragment.querySelector('form#changePasswordForm') as HTMLFormElement;
const submitButton = formFragment.querySelector('button[form=changePasswordForm]') as HTMLButtonElement;
const passwordInput = form.querySelector('input#password') as HTMLInputElement;
const passwordConfirmInput = form.querySelector('input#passwordConfirm') as HTMLInputElement;
const backToLoginButton = formFragment.querySelector('button[data-back-to-login="true"]') as HTMLButtonElement;

const notificationContainer = formFragment.querySelector('div.scw-toast-container[data-toast-notification-container]') as HTMLDivElement;

// Scoped to the input group rather than nextElementSibling: the input sits inside
// a .scw-password-field wrapper, so it has no error-container sibling of its own.
const passwordConfirmInputError = passwordConfirmInput.closest('.scw-input-group')?.querySelector('div.error-container') as HTMLElement;
const passwordConfirmErrorSpan = (errorLabelFragment.content.cloneNode(true) as DocumentFragment).querySelector('span.scw-error-label') as HTMLSpanElement;
passwordConfirmInputError.append(passwordConfirmErrorSpan);

initPasswordToggles(formFragment);

submitButton.innerHTML = 'Update Password ' + (state.environmentReadableName ? ` (${state.environmentReadableName})` : '');


const applyValidationResults = (username: string, password: string) => {
    const {isValid, rules} = checkPasswordRules(username, password);
    Object.entries(rules).forEach(([key, value]) => {
        const icon = form.querySelector('#' + key) as HTMLElement;
        if (!icon) return;

        
        icon.classList.toggle('fa-check-circle', value)
        icon.classList.toggle('fa-times-circle', !value)
    });
    passwordInput.setAttribute('scw-input-valid', String(isValid));
};

const setSubmitButtonStatus = () => {
    submitButton.disabled = !(passwordInput.getAttribute('scw-input-valid') === 'true' && passwordConfirmInput.getAttribute('scw-input-valid') === 'true');
};

const passwordConfirmChange = () => {
    const isValid = passwordInput.value === passwordConfirmInput.value;
    if (!isValid && passwordConfirmInput.value) {
        passwordConfirmErrorSpan.innerHTML = '<i class="fas fa-exclamation-circle mr-1"></i> Please make sure your passwords match.';
    } else {
        passwordConfirmErrorSpan.innerHTML = '';
    }
    passwordConfirmInput.setAttribute('scw-input-valid', String(isValid));
    setSubmitButtonStatus();
};

const passwordChange = () => {
    applyValidationResults(state.username,passwordInput.value);
    passwordConfirmChange();
    setSubmitButtonStatus();
};

backToLoginButton.addEventListener('click', async () => {
    window.location.replace(state.backToLogin);
});

passwordInput.addEventListener('input', debounce(passwordChange, 300));
passwordConfirmInput.addEventListener('input', debounce(passwordConfirmChange, 300));

const changePassEndPoints: Record<PageState["hint"], string> = {
    "change_pass_req": "/api/change-password/",
    "forgot-password" :"/api/forgot-password/change-pass/",
}

onSubmit(form, async (formData) => {
    const data = Object.fromEntries(formData);
    const ruleCheckResult = checkPasswordRules(state.username ,data.password as string);
    const isValid = ruleCheckResult.isValid && data.password === data.passwordConfirm;

    if (!isValid) return;

    const endpoint = changePassEndPoints[state.hint];
    clearContainerElement(notificationContainer);
    // scwFetch throws on any non-2xx response, so reaching this point means the
    // password was changed successfully — no success flag in the body is needed.
    await scwFetch(endpoint + state.state, {
        method: "POST",
        body: {password: data.password},
        notificationContainer
    });

    resetContainers();
    clearDynamicContainer();
    toggleSidePanel(true);
    toggleDynamicContainerMode();
    let changePasswordSuccessFragment = templates.changePasswordSuccess?.content?.cloneNode(true) as DocumentFragment;
    const redirectButton = changePasswordSuccessFragment.querySelector('button') as HTMLButtonElement;
    redirectButton.addEventListener('click', async () => window.location.replace(state.actionUrl));
    dynamicContainer.append(changePasswordSuccessFragment);
}, submitButton);

dynamicContainer.append(formFragment);